import React, { useState, useEffect } from 'react';

import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';

const ServiceModal = ({ isOpen, onClose, service, onSave, categories=[] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    currentAmount: '',
    originalAmount: '',
    duration: '',
    category: '',
    // equipment: [],
    // image: '',
    active: true,
    addOn: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const equipmentOptions = [
    { value: 'pressure-washer', label: 'Pressure Washer' },
    { value: 'foam-cannon', label: 'Foam Cannon' },
    { value: 'microfiber-towels', label: 'Microfiber Towels' },
    { value: 'wax-applicator', label: 'Wax Applicator' },
    { value: 'vacuum-cleaner', label: 'Vacuum Cleaner' },
    { value: 'steam-cleaner', label: 'Steam Cleaner' },
    { value: 'polishing-machine', label: 'Polishing Machine' },
    { value: 'tire-shine', label: 'Tire Shine Kit' }
  ];

  useEffect(() => {
    if (service) {
      setFormData({
        name: service?.name || '',
        description: service?.description || '',
        currentAmount: service?.currentAmount?.toString() || '',
        originalAmount: service?.originalAmount?.toString() || '',
        duration: service?.duration?.toString() || '',
        category: categories.find(x=>x.label == service?.category)?.value || '',
        active: service?.active,
        addOn: service?.addOn || false
      });
    } else {
      setFormData({
        name: '',
        description: '',
        currentAmount: '',
        originalAmount: '',
        duration: '',
        category: '',
        active: true,
        addOn: false
      });
    }
    setErrors({});
  }, [service, isOpen]);

  const validateForm = () => {
    setErrors({});
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Service name is required';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Service description is required';
    }

    if (!formData?.currentAmount || parseFloat(formData?.currentAmount) <= 0) {
      newErrors.currentAmount = 'Valid price is required';
    }

    if (formData?.originalAmount && parseFloat(formData?.originalAmount) > parseFloat(formData?.currentAmount)) {
      newErrors.originalAmount = 'Original price must be higher than current price';
    }

    if (!formData?.duration || parseInt(formData?.duration) <= 0) {
      newErrors.duration = 'Valid duration is required';
    }

    if (!formData?.category) {
      newErrors.category = 'Service category is required';
    }

    setErrors(newErrors);
    console.log('Form validation result', newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      console.log('Form validation failed', errors);
      return;
    }

    setIsLoading(true);
    
    try {
      const serviceData = {
        ...formData,
        currentAmount: parseFloat(formData?.currentAmount),
        originalAmount: formData?.originalAmount ? parseFloat(formData?.originalAmount) : 0,
        duration: parseInt(formData?.duration),
        id: service?.id
      };

      await onSave(serviceData);
      onClose();
    } catch (error) {
      console.error('Error saving service:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1002 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-card border border-border rounded-lg w-full max-w-2xl elevation-3">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              {service ? 'Edit Service' : 'Add New Service'}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
            />
          </div>

          {/* Modal Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Service Name */}
            <Input
              label="Service Name"
              type="text"
              placeholder="Enter service name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            {/* Service Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Service Description *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={4}
                placeholder="Describe the service in detail..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
              />
              {errors?.description && (
                <p className="mt-1 text-sm text-destructive">{errors?.description}</p>
              )}
            </div>

            {/* Pricing Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Current Price"
                type="number"
                placeholder="0.00"
                value={formData?.currentAmount}
                onChange={(e) => handleInputChange('currentAmount', e?.target?.value)}
                error={errors?.price}
                required
              />
              <Input
                label="Original Price (Optional)"
                type="number"
                placeholder="0.00"
                description="For showing discounts"
                value={formData?.originalAmount}
                onChange={(e) => handleInputChange('originalAmount', e?.target?.value)}
                error={errors?.originalPrice}
              />
            </div>

            {/* Duration and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Duration (minutes)"
                type="number"
                placeholder="0"
                value={formData?.duration}
                onChange={(e) => handleInputChange('duration', e?.target?.value)}
                error={errors?.duration}
                required
              />
              <Select
                label="Service Category"
                options={categories}
                value={formData?.category}
                onChange={(value) => handleInputChange('category', value)}
                error={errors?.category}
                required
              />
            </div>

            {/* Service Status */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="active"
                checked={formData?.active}
                onChange={(e) => handleInputChange('active', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="active" className="text-sm font-medium text-foreground">
                Service is active and available for booking
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="extra"
                checked={formData?.addOn}
                onChange={(e) => handleInputChange('addOn', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="active" className="text-sm font-medium text-foreground">
                Extra service (can be added on top of main services)
              </label>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                iconName="Save"
                iconPosition="left"
                iconSize={16}
              >
                {service ? 'Update Service' : 'Create Service'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;